import React from 'react'
import { Outlet } from 'react-router-dom'
import Box from '@mui/material/Box'
import Drawer from '@mui/material/Drawer'
import Toolbar from '@mui/material/Toolbar'
import Typography from '@mui/material/Typography'
import { Link } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useOperationsQueueQuery } from '../../features/api/walletApi'
import SidebarNav from './SidebarNav'
import TopBar from './TopBar'
import Breadcrumbs from './Breadcrumbs'

const drawerWidth = 286
const drawerWidthCollapsed = 72

export default function AdminLayout() {
  const collapsed = useSelector((s) => s.uiShell.sidebarCollapsed)
  const { data: queue } = useOperationsQueueQuery(undefined, { pollingInterval: 60_000 })

  const queueBadges = {
    deposits: queue?.depositsPending,
    withdrawals: queue?.withdrawalsPending,
    kyc: queue?.kycPending,
  }

  const width = collapsed ? drawerWidthCollapsed : drawerWidth

  return (
    <Box
      sx={(theme) => {
        const isDark = theme.palette.mode === 'dark'
        return {
          display: 'flex',
          minHeight: '100vh',
          bgcolor: 'background.default',
          background: isDark
            ? 'radial-gradient(circle at 18% 8%, rgba(91,63,214,0.22), transparent 34%), radial-gradient(circle at 88% 0%, rgba(240,198,106,0.13), transparent 30%), linear-gradient(135deg, rgba(7,9,18,0.96), rgba(14,10,24,0.98))'
            : 'radial-gradient(circle at 18% 8%, rgba(240,198,106,0.34), transparent 34%), radial-gradient(circle at 88% 0%, rgba(91,63,214,0.12), transparent 30%), linear-gradient(135deg, #f9f0dc, #fffaf0)',
        }
      }}
    >
      <Drawer
        variant="permanent"
        sx={{
          width,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: {
            width,
            boxSizing: 'border-box',
            borderRight: (theme) =>
              theme.palette.mode === 'dark'
                ? '1px solid rgba(240,198,106,0.22)'
                : '1px solid rgba(91,63,214,0.16)',
            bgcolor: 'background.paper',
            background: (theme) =>
              theme.palette.mode === 'dark'
                ? 'linear-gradient(180deg, rgba(31,20,47,0.96), rgba(9,11,20,0.98) 48%, rgba(7,9,18,0.98)), radial-gradient(circle at top, rgba(240,198,106,0.18), transparent 36%)'
                : 'linear-gradient(180deg, rgba(255,250,240,0.98), rgba(249,240,220,0.98) 48%, rgba(242,226,195,0.96)), radial-gradient(circle at top, rgba(240,198,106,0.26), transparent 38%)',
            boxShadow: (theme) =>
              theme.palette.mode === 'dark'
                ? '12px 0 36px rgba(0,0,0,0.36), inset -1px 0 0 rgba(255,255,255,0.04)'
                : '12px 0 34px rgba(73,47,25,0.12), inset -1px 0 0 rgba(91,63,214,0.05)',
            color: 'text.primary',
            transition: (t) =>
              t.transitions.create('width', {
                easing: t.transitions.easing.sharp,
                duration: t.transitions.duration.shorter,
              }),
          },
        }}
      >
        <Toolbar
          sx={{
            px: collapsed ? 1 : 2,
            minHeight: 52,
            borderBottom: (theme) =>
              theme.palette.mode === 'dark'
                ? '1px solid rgba(240,198,106,0.2)'
                : '1px solid rgba(91,63,214,0.14)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: collapsed ? 'center' : 'flex-start',
            background: (theme) =>
              theme.palette.mode === 'dark'
                ? 'linear-gradient(135deg, rgba(240,198,106,0.14), rgba(91,63,214,0.11) 46%, transparent)'
                : 'linear-gradient(135deg, rgba(240,198,106,0.28), rgba(91,63,214,0.08) 46%, transparent)',
          }}
          component={Link}
          to="/dashboard"
          style={{ textDecoration: 'none', color: 'inherit' }}
        >
          {!collapsed ? (
            <Box>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 900,
                  letterSpacing: 0.6,
                  lineHeight: 1,
                  color: 'text.primary',
                  textShadow: (theme) =>
                    theme.palette.mode === 'dark' ? '0 2px 14px rgba(240,198,106,0.2)' : '0 2px 12px rgba(91,63,214,0.1)',
                }}
              >
                Royal Flush
              </Typography>
              <Typography variant="caption" sx={{ color: 'secondary.dark', fontWeight: 800, letterSpacing: 1.2 }}>
                ELITE ADMIN
              </Typography>
            </Box>
          ) : (
            <Typography
              variant="subtitle2"
              sx={{
                display: 'grid',
                width: 38,
                height: 38,
                placeItems: 'center',
                borderRadius: '50%',
                border: (theme) =>
                  theme.palette.mode === 'dark'
                    ? '1px solid rgba(240,198,106,0.45)'
                    : '1px solid rgba(91,63,214,0.24)',
                background: (theme) =>
                  theme.palette.mode === 'dark'
                    ? 'radial-gradient(circle, rgba(240,198,106,0.24), rgba(91,63,214,0.18))'
                    : 'radial-gradient(circle, rgba(255,255,255,0.9), rgba(240,198,106,0.28))',
                color: 'secondary.dark',
                fontWeight: 900,
                boxShadow: '0 10px 26px rgba(0,0,0,0.34)',
              }}
            >
              RF
            </Typography>
          )}
        </Toolbar>
        <Box sx={{ overflowY: 'auto', flex: 1 }}>
          <SidebarNav collapsed={collapsed} queue={queueBadges} />
        </Box>
      </Drawer>

      <Box component="main" sx={{ flexGrow: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <TopBar />
        <Box sx={{ p: { xs: 1.5, md: 2.5 }, flex: 1 }}>
          <Breadcrumbs />
          <Outlet />
        </Box>
      </Box>
    </Box>
  )
}
